import React, { useEffect, useState } from 'react';
import Hero from '../components/Hero';
import ContentRow from '../components/ContentRow';
import OriginalCard from '../components/OriginalCard';
import { endpoints, getList } from '../api/tmdb';
import { fetchPublicShows, fetchContinue, fetchFreeMovies } from '../api/services';
import { useApp } from '../context/AppContext';
import { filterForKids } from '../utils/kidsFilter';
import { useDetail } from '../context/DetailContext';
import MovieCard from '../components/MovieCard';
import FreeMovieCard from '../components/FreeMovieCard';
import FreeMovieHero from '../components/FreeMovieHero';

const Row = ({ title, items, variant = 'poster' }) => (
  <section className="mb-10">
    <div className="flex items-end justify-between px-10 mb-4">
      <h2 className="row-title text-xl md:text-2xl font-semibold text-white">{title}</h2>
    </div>
    <div className="row-scroll overflow-x-auto overflow-y-visible px-10 py-6 flex gap-4">
      {items}
    </div>
  </section>
);

const Home = () => {
  const { profile } = useApp();
  const { openShow, open } = useDetail();
  const isKids = !!profile?.is_kids;

  const [hero, setHero] = useState(null);
  const [rows, setRows] = useState({});
  const [originals, setOriginals] = useState([]);
  const [continueItems, setContinueItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [freeMovies, setFreeMovies] = useState([]);
  const [freeHero, setFreeHero] = useState(null);

  useEffect(() => {
    if (isKids) { setFreeMovies([]); setFreeHero(null); return undefined; }
    let mounted = true;
    fetchFreeMovies().then((list) => {
      if (!mounted || !list?.length) return;
      setFreeMovies(list);
      setFreeHero(list[Math.floor(Math.random() * list.length)]);
    }).catch(() => {});
    return () => { mounted = false; };
  }, [isKids]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      try {
        const [trending, popularM, topM, popularTV, upcoming, nowPlaying, topTV, shows, cont] = await Promise.all([
          getList(endpoints.trending('all', 'day')).catch(() => []),
          getList(endpoints.popularMovies).catch(() => []),
          getList(endpoints.topMovies).catch(() => []),
          getList(endpoints.popularTV).catch(() => []),
          getList(endpoints.upcoming).catch(() => []),
          getList(endpoints.nowPlaying).catch(() => []),
          getList(endpoints.topTV).catch(() => []),
          fetchPublicShows().catch(() => []),
          fetchContinue().catch(() => []),
        ]);
        if (!mounted) return;
        const f = (arr) => filterForKids(arr, isKids);
        const baseTrending = f(trending);
        const withBackdrop = baseTrending.filter((t) => t.backdrop_path);
        const pick = withBackdrop[Math.floor(Math.random() * Math.min(5, withBackdrop.length))] || withBackdrop[0];
        setHero(pick);
        setOriginals(shows || []);
        setContinueItems(cont || []);
        setRows({
          'Discover: Trending Now': baseTrending,
          'Discover: Popular Movies': f(popularM),
          'Discover: Top Rated Movies': f(topM),
          'Discover: Popular TV Shows': f(popularTV),
          'Discover: Now Playing': f(nowPlaying),
          'Discover: Coming Soon': f(upcoming),
          'Discover: Top Rated TV': f(topTV),
        });
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [isKids]);

  const continueElems = continueItems.map((c) => {
    if (c.media_type === 'original' && c.show_id) {
      const s = (originals || []).find((o) => o.show_id === c.show_id);
      if (s) return <OriginalCard key={c.show_id} show={s} variant="backdrop" progress={c.progress} />;
      return null;
    }
    return <MovieCard key={`${c.media_type}-${c.tmdb_id}`} item={{
      id: c.tmdb_id, tmdb_id: c.tmdb_id, media_type: c.media_type,
      title: c.title, name: c.title,
      poster_path: c.poster_path, backdrop_path: c.backdrop_path,
      progress: c.progress,
    }} variant="backdrop" />;
  }).filter(Boolean);

  return (
    <div>
      {freeHero ? <FreeMovieHero movie={freeHero} /> : <Hero item={hero} />}
      <div className="relative -mt-24 z-10">
        {continueElems.length > 0 && <Row title="Continue Watching" items={continueElems} variant="backdrop" />}
        {originals.length > 0 && (
          <Row title="BLUNT Originals · Exclusive to BLUNT TV" items={originals.map((s) => <OriginalCard key={s.show_id} show={s} variant="backdrop" />)} variant="backdrop" />
        )}
        {freeMovies.length > 0 && (
          <div data-testid="free-movies-row">
            <Row title="Free Movies" items={freeMovies.map((m) => <FreeMovieCard key={m.id} movie={m} />)} />
          </div>
        )}

        <div className="mx-10 mb-6 px-5 py-3 rounded-xl bg-white/5 border border-white/10 text-sm text-white/70 flex items-center gap-3">
          <span className="text-[#E50914]">★</span>
          <span><span className="text-white font-semibold">Discover rows below</span> feature trailers & info only. Your subscription unlocks full <span className="text-white font-semibold">BLUNT Originals</span> above — plus ad-free trailers and personalized recommendations for 10,000+ titles.</span>
        </div>

        {Object.entries(rows).map(([title, items], i) => (
          <ContentRow
            key={title}
            title={title}
            items={items}
            loading={loading}
            variant={i === 0 ? 'backdrop' : 'poster'}
          />
        ))}

        <div className="px-10 py-8 text-center text-white/40 text-xs">
          Movie & TV metadata provided by <a href="https://www.themoviedb.org/" target="_blank" rel="noreferrer" className="text-white/60 hover:text-white">TMDB</a>.
          BLUNT TV is not endorsed or certified by TMDB. Full-length playback on the Discover rows is provided by the respective studios on their own platforms.
        </div>
      </div>
    </div>
  );
};

export default Home;
